import React, { useContext, useEffect, useState } from 'react';
import TextInput from '../../common/TextInput/TextInput';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import './NeedHelpPage.scss';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import {
  PageAbsolutePath,
  ValidationConstants,
  ValidationMessages
} from '../../../consts/Common';
import Links, { LinkItem } from '../../common/Widgets/Links';
import List from '../../common/Widgets/List';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { SiteContext } from '../../../context/SiteContext';
import UploadNCDDialog from './UploadNCDDialog';
import { FilePondFile } from '../../common/FileUpload/FilepondDefinitions';
import { ApiFile, NCDEmailContent } from '../../common/CommonTypes';
import { FileUploadApi } from '../../../api/FileUploadApi';
import { isValidName } from '../../common/HelperMethods';
import MessageDialog from './MessageDialog';

type ProofNcdFields = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  postCode: string;
};

export type SaveResponse = {
  success: boolean;
  errorMessage: string;
};

const initialFormValues: ProofNcdFields = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  postCode: ''
};

type ProofNcdFieldErrors = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  postCode: string;
};

const initialFormErrors: ProofNcdFieldErrors = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  postCode: ''
};

const successMessage = 'You have uploaded your proof NCD , Thanks.';

const ProofOfNcd = (): JSX.Element => {
  const navigate = useNavigate();

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const token = site.user.token;

  const [formValues, setFormValues] =
    useState<ProofNcdFields>(initialFormValues);

  const [errors, setErrors] = useState<ProofNcdFieldErrors>(initialFormErrors);

  const [saveErrorMessage, setSaveErrorMessage] = useState<string>('');

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [file, setFile] = useState<FilePondFile | undefined>(undefined);

  const { firstName, lastName, emailAddress, postCode } = formValues;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onTextChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const updatedFormValues = { ...formValues, [key]: e.currentTarget.value };

    setFormValues(updatedFormValues);
  };

  const goBack = (): void => {
    navigate(PageAbsolutePath.Insurance.SiteMap);
  };

  const validateNCDFields = (): boolean => {
    let isValid = true;

    const error: ProofNcdFieldErrors = {
      ...initialFormErrors
    };

    if (CommonValidation.isEmpty(lastName)) {
      isValid = false;
      error.lastName = ValidationMessages.InsuranceJourney.lastNameRequiredMsg;
    } else if (
      lastName.length < ValidationConstants.Insurance.lastNameMinCharLimit
    ) {
      isValid = false;
      error.lastName = ValidationMessages.InsuranceJourney.lastNameLimitError;
    } else {
      if (isValidName(lastName)) error.lastName = '';
      else {
        error.lastName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(firstName)) {
      isValid = false;
      error.firstName =
        ValidationMessages.InsuranceJourney.firstnameRequiredMsg;
    } else if (
      !CommonValidation.isValidLength(
        firstName,
        ValidationConstants.Insurance.firstNameCharLimit
      )
    ) {
      isValid = false;
      error.firstName = ValidationMessages.InsuranceJourney.firstNameLimitError;
    } else {
      if (isValidName(firstName)) error.firstName = '';
      else {
        error.firstName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (CommonValidation.isNotEmpty(emailAddress)) {
      if (!CommonValidation.isValidEmail(emailAddress)) {
        error.emailAddress =
          ValidationMessages.InsuranceJourney.invalidEmailMsg;
        isValid = false;
      }
    } else {
      isValid = false;
      error.emailAddress = ValidationMessages.InsuranceJourney.emailRequiredMsg;
    }

    if (CommonValidation.isNotEmpty(postCode)) {
      if (!CommonValidation.isValidPostcode(postCode)) {
        error.postCode = ValidationMessages.InsuranceJourney.postcodeInvalidMsg;
        isValid = false;
      }
    } else {
      isValid = false;
      error.postCode = ValidationMessages.InsuranceJourney.postcodeRequiredMsg;
    }

    setErrors(error);

    return isValid;
  };

  const onFileChange = (files: FilePondFile[]) => {
    const uploadedFile = files && files.length > 0 ? files[0] : undefined;

    setFile(uploadedFile);
  };

  const onClickUploadNcd = () => {
    const isValid = validateNCDFields();

    if (isValid) {
      setShowUploadModal(true);
    }
  };

  const onCloseModal = () => {
    setShowUploadModal(false);
  };

  const saveFileSuccess = (response: SaveResponse) => {
    if (response.success === false) {
      console.log(`Upload file error: ${response.errorMessage}`);
    } else {
      console.log(`File uploaded`);

      setFile(undefined);
      setShowUploadModal(false);
      setShowSuccessModal(true);
    }
  };

  const saveFileFailure = () => {
    setSaveErrorMessage('The uploaded file failed to upload, please try again');
  };

  const onUploadModal = () => {
    const saveFiles = () => {
      const apiFile: ApiFile = {
        filename: file?.filename ?? '',
        filepath: file?.serverId ?? '',
        filetype: file?.fileType ?? ''
      };

      const request: NCDEmailContent = {
        firstName: firstName,
        lastName: lastName,
        email: emailAddress,
        postCode: postCode,
        brandName: brand.brandDisplayName,
        attachment: apiFile
      };

      new FileUploadApi().saveFiles(
        request,
        token,
        saveFileSuccess,
        saveFileFailure
      );
    };

    if (file !== undefined) {
      setSaveErrorMessage('');
      saveFiles();
    }
    console.log('saved');
  };

  const crossComponent = <FontAwesomeIcon icon={faXmark} listItem />;
  const tickComponent = <FontAwesomeIcon icon={faCheck} listItem />;

  const acceptableItems: string[] = [
    "A current Renewal Notice or an official 'Proof of No Claims Discount (NCD)' letter from the most recent insurance policy held",
    'It must be from a UK insurer and not already in use on a live policy',
    'Original or copy documents'
  ];

  const confirmItems: string[] = [
    "The insurer's name",
    'Your name and that you are the Policyholder or Main Driver',
    'Policy number',
    'The number of NCD years applicable at the end of your policy',
    'Policy Expiry/Cancellation Date (your previous policy should have ended before we can apply a NCD to your new policy)'
  ];

  const nonAcceptableItems: string[] = [
    'A Policy Schedule',
    'A Statement of Fact',
    'A Proposal Form',
    'A Certificate of Insurance',
    'NCD that is in use on another policy unless agreed by us',
    'NCD earned as an additional driver',
    'NCD for a policy that ended over 2 years ago',
    'NCD from a motor home or van over 3.5 tonnes',
    'NCD from a motorbike or taxi',
    'NCD from overseas',
    "NCD from a 'multi-user' fleet policy"
  ];

  const linkItems: LinkItem[] = [
    {
      to: PageAbsolutePath.Insurance.SiteMap,
      text: 'Back'
    }
  ];

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  return (
    <div>
      <Links
        id='Proof-ncd'
        title=''
        items={linkItems}
        icon={chevronLeftComponent}
      />
      <div className='table-flex-layout'>
        <div className='form-container'>
          <div className='two-col'>
            <div className='control-label'>First name</div>
            <div className='control'>
              <TextInput
                name='firstname'
                placeholder='First name'
                value={firstName}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'firstName')
                }
                className='input-text w250'
                error={errors.firstName}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>Last name</div>
            <div className='control'>
              <TextInput
                name='lastname'
                placeholder='Last name'
                value={lastName}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'lastName')
                }
                className='input-text w250'
                error={errors.lastName}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>Postcode</div>
            <div className='control'>
              <TextInput
                name='postcode'
                placeholder='Postcode'
                value={postCode}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'postCode')
                }
                className='input-text w250'
                error={errors.postCode}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>Email address</div>
            <div className='control'>
              <TextInput
                name='emailaddress'
                placeholder='Email address'
                value={emailAddress}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'emailAddress')
                }
                className='input-text w250'
                error={errors.emailAddress}
              />
            </div>
          </div>
          <ButtonContinue
            name='btnUploadNcd'
            text='Upload My NCD'
            onClick={onClickUploadNcd}
            additionalClassNames='btn-ok'
          />
        </div>
      </div>
      <hr />
      <p>
        The below information applies to NCD which has been earned on a standard
        UK private car policy. If you are using NCD from a non-UK insurer or
        another type of cover, you must call us to obtain approval before
        arranging the policy. Please see our{' '}
        <a className='in-text' target='_blank' href='docs/Guide to NCD.pdf'>
          {' '}
          Guide to NCD
        </a>
        which outlines our NCD proof requirements for all types of cover.
      </p>
      <h4>What's acceptable?</h4>
      <List id='upload-ncd' listItems={acceptableItems} icon={tickComponent} />
      <h4>It should confirm:</h4>
      <List id='upload-ncd' listItems={confirmItems} icon={tickComponent} />
      <hr />
      <h4>What's not acceptable?</h4>
      <List
        id='upload-ncd'
        listItems={nonAcceptableItems}
        icon={crossComponent}
      />
      <div className='flex-row'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={goBack}
          additionalClassNames='btn-back'
        />
      </div>
      <UploadNCDDialog
        showDialog={showUploadModal}
        onCloseModal={onCloseModal}
        onUploadModal={onUploadModal}
        brandName={brand.brandName}
        file={file}
        onFileChange={(files: FilePondFile[]) => onFileChange(files)}
        saveErrorMessage={saveErrorMessage}
        token={token}
      />
      <MessageDialog
        title='Success'
        messageToDisplay={successMessage}
        showDialog={showSuccessModal}
        onCloseModal={() => setShowSuccessModal(false)}
        brandName={brand.brandName}
      />
    </div>
  );
};

export default ProofOfNcd;
